<template>
  <div>
    <SkeletonFollowersByChannel v-if="isLoading" />

    <ChartBox
      v-if="!isLoading"
      class="followers-by-channel"
      :title="title"
    >
      <ErrorDefault
        :show="errorState"
        @onClickButton="tryAgain()"
      />

      <EmptyState :show="emptyState" />

      <template #primary-action>
        <DownloadMenu
          v-if="!errorState && !isLoading && !emptyState"
          track-name-on-open="BM ExportFanbaseAll Graph Open"
          @selectDownload="handleDownload"
        />
      </template>

      <div
        v-if="!errorState && !isLoading && !emptyState"
        class="followers-by-channel__chart"
      >
        <Pie
          ref="followersByChannelChart"
          :total="totalFollowers"
          :data="data"
          :override-chart-options="overrideChartOptions"
          @legend-click="tracksClickOnChartLegend"
        />
      </div>
    </ChartBox>
  </div>
</template>

<script>
import { dispatchMixpanelTrack, generateFileName, numberFormatToPtBr } from '@/utils';
import formatterLabel from '@/components/content/charts/Pie/formatter-label.js';

import { Pie } from '@/components/content/charts/Pie';
import { EmptyState, ErrorDefault, DownloadMenu, ChartBox } from '@/components/base';
import SkeletonFollowersByChannel from '@/components/content/FollowersByChannel/SkeletonFollowersByChannel.vue';

export default {
  name: 'FollowersByChannel',
  components: {
    ChartBox,
    ErrorDefault,
    EmptyState,
    Pie,
    DownloadMenu,
    SkeletonFollowersByChannel,
  },
  props: {
    chartDetails: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      flw: {},
      isLoading: true,
      errorState: false,
      emptyState: false,
    };
  },
  computed: {
    data() {
      return this.flw.data || [];
    },
    totalFollowers() {
      return this.flw.totalFollowers || 0;
    },
    title() {
      return this.isLoading ? '' : this.flw.label;
    },
    overrideChartOptions() {
      const filename = generateFileName(`${this.chartDetails.titleChart}_${this.chartDetails.groupName}`);
      const totalFollowers = numberFormatToPtBr(this.totalFollowers);

      return {
        exporting: {
          enabled: false,
          allowHTML: true,
          filename,
          chartOptions: {
            title: {
              text: this.chartDetails.titleChart,
            },
            subtitle: {
              text: this.chartDetails.groupName,
            },
            legend: {
              labelFormatter() {
                return formatterLabel(this, true);
              },
              width: 320,
              height: 100,
              align: 'left',
              layout: 'vertical',
              verticalAlign: 'top',
              floating: true,
              useHTML: true,
              y: 200,
              x: 0,
            },
            chart: {
              events: {
                load() {
                  const totalFans = `
                    <div class="total-fans">
                      <span class="total-fans__label">${totalFollowers}</span><br>
                      <span class="total-fans__number">Total de fãs</span>
                    </div>
                  `;

                  this.renderer
                    .text(totalFans, 145, 110)
                    .attr({
                      zIndex: 3,
                      useHTML: true })
                    .add();
                },
              },
            },
          },
          sourceWidth: 360,
          sourceHeight: 360,
        },
      };
    },
  },
  created() {
    this.getChartData();
  },
  methods: {
    async getChartData() {
      this.flw = await this.$store.dispatch('charts/fetchFollowersByChannel', {
        groupName: this.chartDetails.groupName,
        groupId: this.chartDetails.groupId,
      });

      this.emptyState = this.flw.requestStatus === '';
      this.errorState = this.flw.requestStatus === 'error';
      this.isLoading = false;
    },
    tryAgain() {
      this.errorState = false;
      this.isLoading = true;
      this.getChartData(false);
    },
    tracksClickOnChartLegend({ legend }) {
      dispatchMixpanelTrack('BM FanbaseAll LegendGraph Click', ['network'], {
        answer: legend,
      });
    },
    dispatchTrackOnDownload(type) {
      dispatchMixpanelTrack('BM ExportFanbaseAll Graph Click', ['network', 'tab', 'groups'], {
        answer: type,
      });
    },
    handleDownload({ typeSelected, isDownloadFunction, action }) {
      if (!this.$refs.followersByChannelChart) return;

      const followersByChannelChart = this.$refs.followersByChannelChart.$refs.chart;

      this.dispatchTrackOnDownload(typeSelected);

      if (isDownloadFunction) return followersByChannelChart.chart[action]();

      followersByChannelChart.chart.exportChart({ type: action });
    },
  },
};
</script>

<style scoped lang="scss">
.followers-by-channel {
  border: 1px solid var(--ds-color-silver);
  border-radius: var(--ds-spacing-stack-quark);
  padding: var(--ds-spacing-stack-xs);
  overflow: hidden;
  max-height: 325px;
  position: relative;
}

::v-deep {
  .ds-chart-box {
    min-width: auto;

    &__heading {
      gap: var(--ds-spacing-stack-nano);
      margin-bottom: var(--ds-spacing-stack-nano);

      h1 {
        margin-bottom: 0;
      }
    }

    &__title {
      font-weight: var(--ds-font-weight-semibold);
      width: 226px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .ds-icon {
    height: 1em;
  }

  .ds-button--icon {
    padding: 2px;
    height: 30px;
    line-height: 10px;
    width: 30px;
  }

  .download-menu__wrapper-list {
    top: 38px;
  }

  .error-default__wrapper {
    height: 245px;
  }
}
</style>
